// Stundenzettel für die Lohnbuchhaltung.
//
// Einmal im Monat holt sich der Hof hier die CSV und schickt sie dem
// Steuerberater. Wichtig ist nicht die Datei — wichtig ist, dass sie VOLLSTÄNDIG
// ist: Stornierte und nachträglich geänderte Einträge stehen mit drin, samt
// Grund. Eine Aufstellung, aus der Korrekturen spurlos verschwinden, hilft
// niemandem, wenn geprüft wird.

import { useMemo, useState } from "react";
import { useLohnMonat, baueCsv, ladeCsvHerunter } from "@/lib/lohn-export";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, cn } from "@/design-system/ui";

const MONATE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export function LohnTab() {
  const heute = new Date();
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [monat, setMonat] = useState(heute.getMonth() + 1);

  const { data: zeilen = [], isLoading } = useLohnMonat(jahr, monat);

  const proPerson = useMemo(() => {
    const m = new Map<string, { name: string; nr: string; stunden: number; auffaellig: number }>();
    for (const z of zeilen) {
      const g = m.get(z.name) ?? { name: z.name, nr: z.personalnummer, stunden: 0, auffaellig: 0 };
      if (!z.storniert) g.stunden += z.stunden;
      if (z.storniert || z.geaendert) g.auffaellig++;
      m.set(z.name, g);
    }
    return [...m.values()].sort((a, b) => b.stunden - a.stunden);
  }, [zeilen]);

  const gesamt = proPerson.reduce((s, p) => s + p.stunden, 0);
  const ohneNummer = proPerson.filter((p) => !p.nr);

  const blaettern = (schritt: number) => {
    const d = new Date(jahr, monat - 1 + schritt, 1);
    setJahr(d.getFullYear());
    setMonat(d.getMonth() + 1);
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      {/* Monatswahl */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <Button size="sm" onClick={() => blaettern(-1)} aria-label="Vorheriger Monat">
          ←
        </Button>
        <span className="text-[15px] font-medium text-ink-100">
          {MONATE[monat - 1]} {jahr}
        </span>
        <Button
          size="sm"
          onClick={() => blaettern(1)}
          disabled={jahr === heute.getFullYear() && monat === heute.getMonth() + 1}
          aria-label="Nächster Monat"
        >
          →
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LogoLoader size="md" label="Stunden werden geladen" />
        </div>
      ) : zeilen.length === 0 ? (
        <EmptyState
          title="Keine Zeiten"
          hint={`Im ${MONATE[monat - 1]} hat niemand gestempelt.`}
        />
      ) : (
        <>
          <Card className="mb-4 p-4">
            <div className="text-xs uppercase tracking-wider text-ink-400">Gesamt</div>
            <div className="mt-1 text-3xl font-semibold tabular-nums text-ink-50">
              {gesamt.toFixed(2).replace(".", ",")}
              <span className="ml-1.5 text-base font-normal text-ink-400">Stunden</span>
            </div>
            <div className="mt-1 text-sm tabular-nums text-ink-500">
              {proPerson.length} Mitarbeiter · {zeilen.length} Einträge
            </div>
          </Card>

          {/* Ohne Personalnummer kann der Steuerberater nichts zuordnen. Das MUSS
              auffallen, bevor die Datei rausgeht — nicht danach. */}
          {ohneNummer.length > 0 && (
            <p className="mb-4 rounded-lg border border-warn-bg/40 bg-warn-bg/10 px-3 py-2.5 text-sm text-warn-fg">
              Ohne Personalnummer: {ohneNummer.map((p) => p.name).join(", ")}. Im Team-Bereich
              nachtragen — sonst kann die Lohnbuchhaltung die Stunden niemandem zuordnen.
            </p>
          )}

          <ul className="mb-4 space-y-2">
            {proPerson.map((p) => (
              <li key={p.name}>
                <Card className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-medium text-ink-100">{p.name}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                      <span className={cn("font-mono", !p.nr && "text-warn-fg")}>
                        {p.nr || "keine Personalnr."}
                      </span>
                      {p.auffaellig > 0 && (
                        <span className="text-warn-fg">
                          {p.auffaellig} korrigiert/storniert
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-[15px] font-semibold tabular-nums text-ink-50">
                    {p.stunden.toFixed(2).replace(".", ",")} h
                  </div>
                </Card>
              </li>
            ))}
          </ul>

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() =>
              ladeCsvHerunter(
                baueCsv(zeilen, jahr, monat),
                `Stundenzettel_${jahr}-${String(monat).padStart(2, "0")}_Hof-Sirges.csv`
              )
            }
          >
            Als CSV herunterladen
          </Button>

          <p className="mt-2 text-center text-xs text-ink-500">
            Semikolon-getrennt, öffnet direkt in Excel. Stornierte und korrigierte Einträge
            sind mit ausgewiesen.
          </p>
        </>
      )}
    </div>
  );
}
